import { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useGetAccountsQuery } from "features/api/accountApi";
import { useCreateSupportCaseMutation, useGetSupportOrdersQuery } from "../api/supportApi";
import { SupportOrder } from "../api/supportTypes";
import useDebounce from "hooks/useDebounce";
import { RootState } from "stores/application.store";
import { models } from "types/api/viewModels";
import { SupportCaseFormData, supportCaseFormSchema } from "types/forms/supportCaseForm";

const useSupportCaseForm = (user: models["UserViewModel"], isOrderHelp: boolean) => {
	const navigate = useNavigate();
	const emulation = useSelector((state: RootState) => state.impersonation.impersonationUser);
	const isAccountEmulation = Boolean(emulation?.accountId && !emulation.email);
	const [account, setAccount] = useState<models["CustomerAccountViewModel"] | null>(null);
	const [order, setOrder] = useState<SupportOrder | null>(null);
	const [orderSearch, setOrderSearch] = useState("");
	const [files, setFiles] = useState<File[]>([]);
	const search = useDebounce(orderSearch.trim(), 300);
	const accountsQuery = useGetAccountsQuery(undefined);
	const ordersQuery = useGetSupportOrdersQuery({
		accountId: account?.accountId ?? undefined,
		search: search.length >= 3 ? search : undefined
	});
	const [createCase, submission] = useCreateSupportCaseMutation();
	const methods = useForm<SupportCaseFormData>({
		resolver: yupResolver(supportCaseFormSchema),
		defaultValues: {
			problemType: isOrderHelp ? "" : "Other",
			name: isAccountEmulation ? "" : [user.firstName, user.lastName].filter(Boolean).join(" "),
			email: isAccountEmulation ? "" : (user.email ?? ""),
			description: "",
			url: ""
		}
	});

	const changeAccount = (selected: models["CustomerAccountViewModel"] | null) => {
		setAccount(selected);
		setOrder(null);
		setOrderSearch("");
	};

	const submit = methods.handleSubmit(async (values) => {
		if (submission.isLoading || accountsQuery.isError || ordersQuery.isError) return;
		try {
			await createCase({
				...values,
				caseType: isOrderHelp ? "Order Help" : "Problem",
				problemType: isOrderHelp ? values.problemType : "Other",
				accountId: account?.accountId ?? undefined,
				accountNumber: account?.accountNumber ?? undefined,
				orderId: order?.orderId ?? undefined,
				orderNumber: order?.orderNumber ?? undefined,
				attachments: files
			}).unwrap();
			navigate("/support/request");
		} catch {
			// Keep the form and attachments; the mutation error renders the failure banner.
		}
	});

	return {
		methods,
		account,
		order,
		orderSearch,
		files,
		accountsQuery,
		ordersQuery,
		submission,
		submit,
		changeAccount,
		setOrder,
		setOrderSearch,
		setFiles
	};
};

export default useSupportCaseForm;
